import { FC, ReactNode, useEffect, useMemo } from 'react';

import { DrivelineViewDto, ProductIndividualDto } from 'api';
import { getChassisId } from 'api/responses/models/Unit/UnitStructure/DrivelineSubSystem';
import {
	DrivelineContent,
	DrivelineHeader,
	DrivelineWrapper,
} from 'library/components/controls/cards/DrivelineWrapper';
import { useAppDispatch } from 'library/redux/hooks';
import { Disable, Readonly, Sxable } from 'library/types';
import { sortByPosition } from 'library/utils/helpers/Installation';
import { getProductIndividualCommentsCount } from 'library/utils/helpers/Unit';
import { ProductIndividualComments } from 'modules/Shared/Components/ProductIndividual/Comments/ProductIndividualComments';
import {
	mapForeignProductIndividualItemPurposeToDto,
	mapProductIndividualItemPurposeToProductIndividualDto,
} from 'modules/Shared/State/unit';
import {
	DrivelineTitleLink,
	defaultDrivelineTitleLinkSx,
} from 'modules/UnitCreation/SharedComponents/Driveline/DrivelineTitleLink';
import { setProductIndividualsCommentsCount } from 'modules/UnitOverview/State/Slice';

import { ForeignProductIndividualCard } from '../ProductIndividual/ForeignProductIndividualCard';
import { ProductIndividualCard } from '../ProductIndividual/ProductIndividualCard';

export type ProductIndividualItemPurposeProps = {
	product: ProductIndividualDto;
};

interface DrivelineItemViewProps extends Disable, Readonly, Sxable {
	driveline: DrivelineViewDto;
	headerActions?: ReactNode;
	installationId?: string;
	ProductIndividualCardActions?: FC<ProductIndividualItemPurposeProps>;
	withLink?: boolean;
}

const DrivelineItemView = ({
	disabled = false,
	driveline,
	headerActions,
	isReadonly = false,
	installationId,
	sx: wrapperSx,
	ProductIndividualCardActions,
	withLink = true,
}: DrivelineItemViewProps) => {
	const dispatch = useAppDispatch();

	const sortedProductIndividuals = useMemo(
		() =>
			sortByPosition(
				driveline.productIndividualSubSystems.flatMap(
					({ productIndividuals }) => productIndividuals
				)
			).map(mapProductIndividualItemPurposeToProductIndividualDto),
		[driveline.productIndividualSubSystems]
	);

	const sortedForeignProductIndividuals = useMemo(
		() =>
			sortByPosition(
				driveline.productIndividualSubSystems.flatMap(
					({ foreignProductIndividuals }) => foreignProductIndividuals
				)
			).map(mapForeignProductIndividualItemPurposeToDto),
		[driveline.productIndividualSubSystems]
	);

	useEffect(() => {
		const allItemPurposes = driveline.productIndividualSubSystems.flatMap(
			(s) => [...s.productIndividuals, ...s.foreignProductIndividuals]
		);
		dispatch(
			setProductIndividualsCommentsCount(
				getProductIndividualCommentsCount(allItemPurposes)
			)
		);
	}, [dispatch, driveline.productIndividualSubSystems]);

	return (
		<DrivelineWrapper sx={wrapperSx}>
			<DrivelineHeader
				title={
					<DrivelineTitleLink
						chassisId={getChassisId(driveline)}
						drivelineId={driveline.id}
						isActive={withLink}
						sx={defaultDrivelineTitleLinkSx}
					/>
				}
				actions={headerActions}
			/>
			<DrivelineContent>
				{sortedProductIndividuals.map((product) => (
					<ProductIndividualCard
						actions={
							<>
								<ProductIndividualComments
									id={product.id}
									disabled={disabled}
									drivelineId={driveline.id.toString()}
									generatedUuid={product.serialNumber}
									installationId={installationId || ''}
									isForeign={false}
									isReadonly={isReadonly}
									productId={product.serialNumber}
									serialNumber={product.serialNumber}
								/>
								{ProductIndividualCardActions && (
									<ProductIndividualCardActions
										product={product}
									/>
								)}
							</>
						}
						key={`product-individual-${product.serialNumber}`}
						product={product}
					/>
				))}
				{sortedForeignProductIndividuals.map((product) => (
					<ForeignProductIndividualCard
						actions={
							<ProductIndividualComments
								id={product.id}
								disabled={disabled}
								drivelineId={driveline.id.toString()}
								generatedUuid={product.serialNumber}
								installationId={installationId || ''}
								isForeign
								isReadonly={isReadonly}
								productId={product.serialNumber}
								serialNumber={product.serialNumber}
							/>
						}
						key={`foreign-product-individual-${product.serialNumber}`}
						product={product}
					/>
				))}
			</DrivelineContent>
		</DrivelineWrapper>
	);
};

export default DrivelineItemView;
